/**
 * Utility functions for city name normalization and matching
 */
import { debugLog } from '@/utils/debugLogger';

// Popular cities that appear in the "Compro Oro per città" cards
export const POPULAR_CITIES = [
  "Roma", "Milano", "Napoli", "Torino", 
  "Palermo", "Genova", "Bologna", "Firenze", 
  "Bari", "Catania", "Venezia", "Verona"
] as const;

export type PopularCity = typeof POPULAR_CITIES[number];

/**
 * Check if a city is one of the popular cities shown in cards
 */
export const isPopularCity = (cityName: string): boolean => {
  if (!cityName) return false;
  
  const normalizedInput = cityName.toLowerCase().trim();
  return POPULAR_CITIES.some(city => 
    city.toLowerCase() === normalizedInput
  );
};

/**
 * Get normalized city name for comparison
 */
export const normalizeCityName = (cityName: string): string => {
  return cityName.toLowerCase().trim();
};

/**
 * Extract unique cities from a list of dealers
 */
export const extractCitiesFromDealers = (dealers: any[]): string[] => {
  if (!dealers || !Array.isArray(dealers)) return [];
  
  const cities = new Set<string>();
  
  dealers.forEach(dealer => {
    if (dealer?.city && typeof dealer.city === 'string') {
      cities.add(dealer.city.trim());
    }
  });
  
  return Array.from(cities).sort();
};

/**
 * Get all privileged cities (popular + from current dealers)
 */
export const getPrivilegedCities = (dealers: any[]): string[] => {
  const dealerCities = extractCitiesFromDealers(dealers);
  const allCities = new Set([...POPULAR_CITIES, ...dealerCities]);
  return Array.from(allCities);
};

/**
 * Normalizza il nome di una città per il matching
 * Rimuove caratteri speciali, converte in lowercase, gestisce apostrofi e trattini
 */
export function normalizeCity(city: string): string {
  if (!city) return '';
  
  return city
    .toLowerCase()
    .trim()
    // Rimuovi apostrofi
    .replace(/'/g, '')
    .replace(/'/g, '') // apostrofo curvo
    .replace(/`/g, '')
    // Sostituisci trattini con spazi
    .replace(/-/g, ' ')
    // Rimuovi caratteri speciali extra
    .replace(/[^\w\s]/g, '')
    // Normalizza spazi multipli
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Genera varianti di una città per il matching
 * Include versioni con e senza apostrofi, con trattini e spazi
 */
export function generateCityVariants(city: string): string[] {
  if (!city) return [];
  
  const normalized = normalizeCity(city);
  const variants = new Set<string>();
  
  // Versione normalizzata
  variants.add(normalized);
  
  // Versione originale (lowercase)
  variants.add(city.toLowerCase().trim());
  
  // **CRITICAL FIX for Montecatini**: Aggiungi TUTTE le combinazioni spazi/trattini
  const original = city.toLowerCase().trim();
  
  // Versione con trattini → spazi (montecatini-terme → montecatini terme)
  const withSpaces = original.replace(/-/g, ' ');
  variants.add(withSpaces);
  variants.add(normalizeCity(withSpaces));
  
  // Versione con spazi → trattini (montecatini terme → montecatini-terme)  
  const withHyphens = original.replace(/\s+/g, '-');
  variants.add(withHyphens);
  variants.add(normalizeCity(withHyphens));
  
  // Versione senza separatori (montecatini-terme → montecatiniterme)
  const noSeparators = original.replace(/[\s-]/g, '');
  variants.add(noSeparators);
  variants.add(normalizeCity(noSeparators));
  
  // Versioni del normalized con diverse combinazioni
  variants.add(normalized.replace(/\s+/g, '-')); // spazi → trattini
  variants.add(normalized.replace(/\s+/g, '')); // rimuovi spazi
  
  // Gestione speciale per nomi con "sant", "santa", "santo"
  const cityLower = city.toLowerCase();
  if (cityLower.includes('sant-') || cityLower.includes('santa-') || cityLower.includes('santo-')) {
    // Converti "sant-" in "sant'" e varianti
    const withApostrophe = cityLower
      .replace(/sant-/g, "sant'")
      .replace(/santa-/g, "santa ")
      .replace(/santo-/g, "santo ")
      .replace(/-/g, ' ');
    variants.add(withApostrophe);
    
    // Versione normalizzata con apostrofo
    const normalizedWithApostrophe = normalizeCity(withApostrophe);
    variants.add(normalizedWithApostrophe);
    
    // Versione con apostrofo ma senza altri spazi
    const apostropheNoSpaces = withApostrophe.replace(/\s+/g, '');
    variants.add(apostropheNoSpaces);
  }
  
  // Se contiene spazi, aggiungi versioni con apostrofi
  if (normalized.includes(' ')) {
    const words = normalized.split(' ');
    if (words.length >= 2) {
      // Versione con apostrofo dopo la prima parola
      variants.add(words[0] + "'" + words.slice(1).join(''));
      variants.add(words[0] + "'" + words.slice(1).join(' '));
      
      // Gestione speciale per "sant", "santa", "santo"
      if (words[0] === 'sant' || words[0] === 'santa' || words[0] === 'santo') {
        variants.add(words[0] + "'" + words.slice(1).join(' '));
        variants.add(words[0] + "'" + words.slice(1).join(''));
      }
    }
  }
  
  // Aggiungi varianti specifiche per nomi comuni italiani
  const commonPatterns = [
    { from: /\bsant\s+/g, to: "sant'" },
    { from: /\bsanta\s+/g, to: "santa " },
    { from: /\bsanto\s+/g, to: "santo " },
    { from: /\bsan\s+/g, to: "san " }
  ];
  
  commonPatterns.forEach(pattern => {
    const variant = normalized.replace(pattern.from, pattern.to);
    if (variant !== normalized) {
      variants.add(variant);
      variants.add(variant.replace(/\s+/g, ''));
      variants.add(variant.replace(/\s+/g, '-'));
    }
  });
  
  return Array.from(variants).filter(v => v.length > 0);
}

/**
 * Verifica se una città del database corrisponde alla città cercata
 * Usa matching flessibile ma preciso
 */
export function matchesCity(dealerCity: string, searchCity: string): boolean {
  if (!dealerCity || !searchCity) {
    return false;
  }
  
  const normalizedDealer = normalizeCity(dealerCity);
  const searchVariants = generateCityVariants(searchCity);
  
  // Matching esatto con una delle varianti
  for (const variant of searchVariants) {
    const normalizedVariant = normalizeCity(variant);
    if (normalizedDealer === normalizedVariant) {
      return true;
    }
  }
  
  // Fallback: Matching con solo caratteri alfanumerici (rimuove spazi e punteggiatura)
  const dealerNormalized = dealerCity.toLowerCase().replace(/[^\w]/g, '');
  const searchNormalized = searchCity.toLowerCase().replace(/[^\w]/g, '');
  
  if (dealerNormalized === searchNormalized) {
    return true;
  }
  
  // Fallback: Matching per parole (utile per città composte)
  const dealerWords = normalizedDealer.split(' ');
  const searchCityNormalized = normalizeCity(searchCity);
  
  // Verifica se la città cercata è una parola completa nella città del dealer
  return dealerWords.includes(searchCityNormalized);
}

/**
 * Mapping esplicito per città con apostrofi e casi speciali
 */
const SPECIAL_CITY_MAPPINGS: Record<string, string> = {
  'laquila': "L'Aquila",
  'santagata': "Sant'Agata",
  'santantonio': "Sant'Antonio",
  'santangelo': "Sant'Angelo",
  'santangeloinformis': "Sant'Angelo In Formis",
  'santamaria': "Santa Maria",
  'santarita': "Santa Rita",
  'santagostino': "Sant'Agostino",
  'santarsiero': "Sant'Arsiero",
  'santapollinare': "Sant'Apollinare",
  'santolcese': "Sant'Olcese",
  'santomero': "Sant'Omero",
  'santaandrea': "Sant'Andrea",
  'santamargherita': "Santa Margherita",
  'santacaterina': "Santa Caterina",
  'santasusanna': "Santa Susanna",
  'santamarinella': "Santa Marinella",
  'santacrocesullarno': "Santa Croce sull'Arno",
  'pomiglianodarco': "Pomigliano d'Arco",
  'dellaquila': "Dell'Aquila",
  // Città con apostrofi
  'colledivaldessa': "Colle di Val d'Elsa",
  'caluscodadda': "Calusco d'Adda",
  'santantimo': "Sant'Antimo",
  'santarpino': "Sant'Arpino",
  'casteldazzano': "Castel D'Azzano",
  // Città con trattini
  'cascinelacroce': "Cascine-la Croce",
  'chirignagozelarino': "Chirignago-Zelarino",
  // Città con caratteri speciali
  'areaexferrari': "Area \"ex Ferrari\"",
  'locsanzeno': "Loc. San Zeno",
  // Città con congiunzioni
  'massaecozzile': "Massa e Cozzile",
  // Città con apostrofi in posizioni specifiche
  'quartusantelena': "Quartu Sant'Elena",
  'portosantelpidio': "Porto Sant'Elpidio",
  'portoselpidio': "Porto Sant'Elpidio",
  'morrodoro': "Morro d'Oro",
  'santagnello': "Sant'Agnello",
  'santantioco': "Sant'Antioco",
  'santegidioallavibrata': "Sant'Egidio alla Vibrata",
  'colledivaldelsa': "Colle di Val d'Elsa",
  // Città con trattini e casi specifici
  'arbizzanosantamaria': "Arbizzano-Santa Maria",
  'farafiliorumpetri': "Fara Filiorum Petri",
  'montecatiniterme': "Montecatini Terme",
  // Reggio Emilia variants
  'reggionellemilia': "Reggio nell'Emilia",
  'reggioemilia': "Reggio Emilia",
  // Città con apostrofi (report SEO)
  'palazzolosulloglio': "Palazzolo sull'Oglio",
  'anzoladellemilia': "Anzola dell'Emilia",
  'cavadetirreni': "Cava de' Tirreni",
  'castelnovonemoni': "Castelnovo ne' Monti",
  'castelnovo-nemonti': "Castelnovo ne' Monti",
  'castelnovonemonti': "Castelnovo ne' Monti",
  // Città composte (report SEO)
  'spicchiosovigliana': "Spicchio-Sovigliana",
  'spicchiosovigiana': "Spicchio-Sovigliana",
  'rosignanosolvaycastiglioncello': "Rosignano Solvay-Castiglioncello",
  'rosignanosolvay': "Rosignano Solvay",
  'bellariaigea': "Bellaria-Igea Marina",
  'bellariaigea marina': "Bellaria-Igea Marina",
  'bellariaigeamarina': "Bellaria-Igea Marina"
};

/**
 * Converte un URL slug in nome città formattato
 * Es: "sant-ambrogio-di-valpolicella" -> "Sant'Ambrogio di Valpolicella"
 * Es: "laquila" -> "L'Aquila"
 */
export function formatCityFromSlug(slug: string): string {
  if (!slug) return '';
  
  // Controlla prima il mapping esplicito per casi speciali
  const normalizedSlug = slug.toLowerCase().replace(/-/g, '');
  if (SPECIAL_CITY_MAPPINGS[normalizedSlug]) {
    debugLog('general', `Special mapping: "${slug}" → "${SPECIAL_CITY_MAPPINGS[normalizedSlug]}"`, undefined, 'formatCityFromSlug');
    return SPECIAL_CITY_MAPPINGS[normalizedSlug];
  }
  
  // Logica standard per altre città
  return slug
    .split('-')
    .map(word => {
      // Gestione speciale per articoli e preposizioni
      const lowerWord = word.toLowerCase();
      if (['di', 'del', 'della', 'dei', 'delle', 'da', 'dal', 'dalla', 'in', 'su', 'per'].includes(lowerWord)) {
        return lowerWord;
      }
      
      // Capitalizza la prima lettera
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ')
    // Gestione apostrofi per nomi comuni
    .replace(/\bsant\s+/gi, "Sant'")
    .replace(/\bsanta\s+/gi, "Santa ")
    .replace(/\bsanto\s+/gi, "Santo ");
}

/**
 * Normalizza i caratteri accentati in caratteri ASCII equivalenti
 * Es: "Nicolò" → "Nicolo", "Città" → "Citta"
 */
export function normalizeAccents(str: string): string {
  if (!str) return '';
  
  const accentMap: Record<string, string> = {
    'à': 'a', 'á': 'a', 'â': 'a', 'ã': 'a', 'ä': 'a', 'å': 'a',
    'è': 'e', 'é': 'e', 'ê': 'e', 'ë': 'e',
    'ì': 'i', 'í': 'i', 'î': 'i', 'ï': 'i',
    'ò': 'o', 'ó': 'o', 'ô': 'o', 'õ': 'o', 'ö': 'o',
    'ù': 'u', 'ú': 'u', 'û': 'u', 'ü': 'u',
    'ñ': 'n', 'ç': 'c',
    'À': 'A', 'Á': 'A', 'Â': 'A', 'Ã': 'A', 'Ä': 'A', 'Å': 'A',
    'È': 'E', 'É': 'E', 'Ê': 'E', 'Ë': 'E',
    'Ì': 'I', 'Í': 'I', 'Î': 'I', 'Ï': 'I',
    'Ò': 'O', 'Ó': 'O', 'Ô': 'O', 'Õ': 'O', 'Ö': 'O',
    'Ù': 'U', 'Ú': 'U', 'Û': 'U', 'Ü': 'U',
    'Ñ': 'N', 'Ç': 'C'
  };
  
  return str.split('').map(char => accentMap[char] || char).join('');
}

/**
 * Normalizza testo per ricerca accent-insensitive
 * Es: "Cantù" → "cantu", "Città" → "citta"
 */
export function normalizeForSearch(text: string): string {
  if (!text) return '';
  return normalizeAccents(text).toLowerCase().trim();
}

/**
 * Genera varianti accentate per una città (supporta nomi multi-parola)
 * Utile per il matching accent-insensitive nel database
 * Es: "citta-di-castello" → ["citta di castello", "città di castello", ...]
 */
export function generateAccentVariants(text: string): string[] {
  if (!text) return [];
  
  const variants = new Set<string>();
  const normalized = text.toLowerCase().trim();
  variants.add(normalized);
  
  // Converti trattini in spazi per matching con database
  const withSpaces = normalized.replace(/-/g, ' ');
  variants.add(withSpaces);
  
  // Mappa: carattere non accentato → possibili varianti accentate italiane
  const italianAccentMap: Record<string, string[]> = {
    'a': ['à'],
    'e': ['è', 'é'],
    'i': ['ì', 'í'],
    'o': ['ò', 'ó'],
    'u': ['ù', 'ú']
  };
  
  // Pattern italiani con apostrofo: sant → sant', d → d', nell → nell'
  const apostrophePatterns: Record<string, string[]> = {
    'sant': ["sant'", "santa ", "santo "],
    'santa': ["sant'", "santa "],
    'santo': ["sant'", "santo "],
    'd': ["d'", "d "],
    'nell': ["nell'", ""]  // nell → nell' oppure rimuovi
  };
  
  // Helper: genera varianti per una singola parola (accenti + apostrofi)
  const generateWordVariants = (word: string): string[] => {
    const wordVariants: string[] = [word];
    
    // Varianti accentate (ultima vocale)
    const lastChar = word.slice(-1);
    if (italianAccentMap[lastChar]) {
      italianAccentMap[lastChar].forEach(accentedChar => {
        wordVariants.push(word.slice(0, -1) + accentedChar);
      });
    }
    
    // Varianti con apostrofo per pattern italiani
    for (const [pattern, replacements] of Object.entries(apostrophePatterns)) {
      if (word.startsWith(pattern) && word.length > pattern.length) {
        const suffix = word.slice(pattern.length);
        replacements.forEach(replacement => {
          wordVariants.push(replacement + suffix);
        });
      }
    }
    
    return wordVariants;
  };
  
  // Genera combinazioni per tutte le parole
  const words = withSpaces.split(' ');
  const wordVariantsList = words.map(generateWordVariants);
  
  // Genera tutte le combinazioni (limita per evitare esplosione)
  const generateCombinations = (arrays: string[][]): string[] => {
    if (arrays.length === 0) return [];
    if (arrays.length === 1) return arrays[0];
    
    let result: string[] = arrays[0];
    for (let i = 1; i < arrays.length; i++) {
      const newResult: string[] = [];
      for (const prefix of result) {
        for (const suffix of arrays[i]) {
          newResult.push(`${prefix} ${suffix}`);
        }
      }
      result = newResult;
      // Limita a 50 combinazioni per evitare query troppo grandi
      if (result.length > 50) break;
    }
    return result;
  };
  
  const combinations = generateCombinations(wordVariantsList);
  combinations.forEach(combo => variants.add(combo));
  
  // Gestione specifica per "nell" (reggio nellemilia → reggio nell'emilia, reggio emilia)
  if (withSpaces.includes('nell')) {
    // Con apostrofo: reggio nell'emilia
    variants.add(withSpaces.replace(/\bnell\s*(\w)/g, "nell'$1"));
    variants.add(withSpaces.replace(/\bnell(\w)/g, "nell'$1"));
    
    // Senza "nell" completamente: reggio emilia
    const withoutNell = withSpaces.replace(/\s*nell\s*/g, ' ').replace(/\s+/g, ' ').trim();
    variants.add(withoutNell);
  }
  
  return Array.from(variants);
}

/**
 * Converte un nome città in URL slug
 * Es: "Sant'Ambrogio di Valpolicella" -> "sant-ambrogio-di-valpolicella"
 * Es: "San Nicolò a Tordino" -> "san-nicolo-a-tordino"
 */
export function cityToSlug(city: string): string {
  if (!city) return '';
  
  return normalizeAccents(city)
    .toLowerCase()
    .trim()
    // Rimuovi apostrofi e altri caratteri speciali, sostituisci spazi con trattini
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    // Rimuovi trattini multipli
    .replace(/-+/g, '-')
    // Rimuovi trattini all'inizio e alla fine
    .replace(/^-+|-+$/g, '');
}
